import React, { useContext, useEffect, useMemo, useState } from "react";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import styles from "./styles/AssetDashboard.module.css";
import SectionShell from "./SectionShell";
import Donut from "./Donut";
import ValuationModal from "./ValuationModal";
import EvaluationChart from "../Asset Evaluation/EvaluationChart";
import { useReportContent } from "./reportContent";
import { UserData } from "../../../Context/DataContext";
import { relationshipLabel } from "../../../Utils/minorBeneficiary";
import { getAssetTypeTranslated } from "../../../Utils/translationUtils";
import {
  CURRENCY_SYMBOLS,
  formatCompactMoney,
} from "../../../Utils/assetEvaluationUtils";
import {
  netWorthOf,
  liabilitiesOf,
  liquiditySplitOf,
  categoryGroupsOf,
  exposureAllocationOf,
  exposureGroupsOf,
  valueTimelineOf,
  formatMoney,
  formatChange,
  changeDirection,
  formatRatio,
  wealthChangeOf,
  exposureSectionsOf,
  geographyOf,
  annualIncomeOf,
  incomeBreakdownOf,
  sharingByBeneficiaryOf,
  formatSignedChange,
} from "./reportMetrics";
import {
  SUMMARY_STATS, RATIOS,
  ESTATE_STEPS,
  RECOMMENDATIONS, CHART_COLORS,
} from "./reportData";

/*
  Sections 1-9 of the Wealth Report, plus the strip that closes it.

  Sections 1-7 read the user's own assets through reportMetrics, filtered by the
  report's date range and converted into the currency it is being read in. What
  is left in reportData.js is the design's placeholder for the parts that have
  no data behind them yet: the net-worth trend line, and sections 8 and 9.
*/

const FALLBACK = { assetDashboardText14: "Hide", assetDashboardText15: "Show" };

/*
  The five built-in exposures are worded by the asset panel, so the report reads
  exactly the same names the user picked from — restating them here would let
  the two drift.
*/
const EXPOSURE_LABEL_KEYS = {
  real_estate: "assetEvaluationText20",
  stocks: "assetEvaluationText21",
  bonds: "assetEvaluationText22",
  private_equity: "assetEvaluationText23",
  crypto: "assetEvaluationText19",
};

// Up or down is in the arrow as well as the colour, never colour alone.
/*
  The arrow shows which way the figure moved; the colour shows whether that is
  good, which is not the same thing. Debt going up is a rise and a worsening, so
  `invert` lets a tile say ▲ in red.

  `up === null` is a flat period: "0.0%" with no arrow and no verdict, because
  nothing moved. It is still shown — that the value held steady is an answer,
  and not the same as having nothing to compare it against, which shows nothing
  at all.
*/
const Delta = ({ delta, up, invert = false }) => {
  if (!delta) return null;
  if (up === null || up === undefined) {
    return <span className={`${styles.delta} ${styles.delta_flat}`}>{delta}</span>;
  }
  const good = invert ? !up : up;
  return (
    <span className={`${styles.delta} ${good ? styles.delta_up : styles.delta_down}`}>
      {up ? "▲" : "▼"} {up ? "" : "-"}{delta}
    </span>
  );
};

/*
  The exposure names: a built-in is named by the asset panel, a custom one by
  whatever the user typed.

  useCallback, and it matters. The breakdown sections feed their titles upward
  through a memo and an effect, so a resolver rebuilt on every render would
  recompute that memo every render, push a fresh array up, and re-render
  forever. Both language blocks come from context and hold their identity
  between renders, so keyed on them this is stable.
*/
const useExposureNames = () => {
  const websiteCurrentLang = useContext(LanguageContext).language;
  const evaluation = websiteCurrentLang?.content?.User?.EditAsset?.AssetEvaluation;
  return React.useCallback(
    (group) =>
      group?.builtIn
        ? (evaluation && evaluation[EXPOSURE_LABEL_KEYS[group.key]]) || group.key
        : group?.name,
    [evaluation]
  );
};

/*
  An asset type's display name.

  Unlike an exposure there is no options list to consult: a custom type is
  stored as its own label, so the key IS the name until the language files
  have something better. getAssetTypeTranslated does exactly that -- looks up
  the built-ins and hands anything else straight back -- and is what the
  tables name their type column with, so the report and Asset Management
  cannot end up calling one category two things.
*/
const useCategoryNames = () => {
  const websiteCurrentLang = useContext(LanguageContext).language;
  const dup = websiteCurrentLang?.content?.DublicatedText;
  return React.useCallback(
    (key) => getAssetTypeTranslated(key, null, dup || {}),
    [dup]
  );
};

/*
  A beneficiary's photo, stored as raw base64 with no data-URL prefix. The first
  character of the encoding identifies the format — "i" begins a PNG header, "/"
  a JPEG one — which is the same test the Inheritance Tree makes.

  Null when there is no photo, so the caller falls back to initials.
*/
const avatarSrc = (raw) => {
  const data = typeof raw === "string" ? raw.trim() : "";
  if (!data) return null;
  return `data:image/${data[0] === "i" ? "png" : "jpeg"};base64,${data}`;
};

const initialsOf = (name) =>
  String(name || "")
    .replace(/\(.*\)/, "")
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

/* ============================================================ 1 executive */

export function ExecutiveSummary({ number, range, rates }) {
  const t = useReportContent(FALLBACK);
  const userData = UserData();
  const nameOf = useExposureNames();

  /*
    Net worth is the one figure here that is real. It is the sum of the assets
    that actually carry a valuation — an asset nobody has valued is left out
    rather than counted as nothing — taken at the report's "till" date and
    converted into the currency it is being read in. See reportMetrics.js.

    The other four tiles are still the design's placeholders.
  */
  const currency = (range && range.currency) || "eur";
  /*
    The asset list is read inside the memo, not beside it: `|| []` builds a new
    array on every render, which as a dependency would defeat the memo entirely.
  */
  const figures = React.useMemo(() => {
    const assets = ((userData && userData.UserData) || {}).assets || [];
    const opts = {
      from: range?.from || "",
      till: range?.till || "",
      currency,
      rates,
    };
    return {
      worth: netWorthOf(assets, opts),
      debt: liabilitiesOf(assets, opts),
      split: liquiditySplitOf(assets, opts),
      wealthChange: wealthChangeOf(assets, opts),
      geography: geographyOf(assets, opts),
      income: annualIncomeOf(assets, opts),
      allocation: exposureAllocationOf(
        assets,
        opts,
        ((userData && userData.UserData) || {}).financial_exposure_custom_options
      ),
    };
  }, [userData, range?.from, range?.till, currency, rates]);

  /*
    All five tiles read the user's own data. Annual income is the total of the
    assets' income blocks, a monthly figure counted as twelve of itself — see
    annualIncomeOf. It carries no change indicator: an income block records a
    standing figure rather than a reading taken on a date, so there are no two
    points in the range to compare.

    Liquidity and Illiquid are the same valued assets as Net worth, sorted by
    the asset's liquid flag — so the two of them add back to it. Neither shows
    a change: the design gives one only to Net worth, and a split has no
    meaningful trend of its own.

    No change is shown rather than a made-up one whenever nothing was recorded
    at both ends of the range.
  */
  const computed = {
    net_worth: {
      // The badge follows the currency the report is being read in.
      badge: CURRENCY_SYMBOLS[currency] || null,
      value: formatMoney(figures.worth.total, currency),
      delta: formatChange(figures.worth.change),
      up: changeDirection(figures.worth.change),
    },
    liabilities: {
      value: formatMoney(figures.debt.total, currency),
      delta: formatChange(figures.debt.change),
      up: changeDirection(figures.debt.change),
      // More owed is worse, so the arrow and the colour disagree on purpose.
      invert: true,
    },
    income: { value: formatMoney(figures.income.total, currency), delta: null },
    liquidity: { value: formatMoney(figures.split.liquid.total, currency), delta: null },
    illiquid: { value: formatMoney(figures.split.illiquid.total, currency), delta: null },
  };

  const geography = figures.geography;
  // The longest bar fills its track; the rest are drawn against it.
  const widestGeo = Math.max(...geography.rows.map((r) => r.pct), 1);

  const allocation = figures.allocation.slices.map((slice) => ({
    ...slice,
    name: nameOf(slice),
  }));

  const stats = SUMMARY_STATS.map((item) => {
    const real = computed[item.key];
    if (!real) return item;
    return { ...item, ...real, badge: real.badge || item.badge };
  });

  /*
    The two ratios are read off the tiles above rather than recomputed: debt
    against net worth, liquidity against net worth. The date range is already
    baked into those two figures, so doing the window work again here could only
    produce a ratio that disagrees with the numbers it is quoting.

    Overall return is still the design's placeholder.
  */
  const ratios = RATIOS.map((item) => {
    if (item.key === "debt") {
      return { ...item, value: formatRatio(figures.debt.total, figures.worth.total) };
    }
    if (item.key === "liquidity") {
      return {
        ...item,
        value: formatRatio(figures.split.liquid.total, figures.worth.total),
      };
    }
    if (item.key === "return") {
      /*
        Wealth Change: the whole estate now against the whole estate when the
        period opened. Signed, and coloured, because this field has no arrow of
        its own to say which way it went.
      */
      return {
        ...item,
        value: formatSignedChange(figures.wealthChange),
        tone:
          figures.wealthChange === null ||
          Math.abs(figures.wealthChange).toFixed(1) === "0.0"
            ? null
            : figures.wealthChange > 0,
      };
    }
    return item;
  });

  return (
    <SectionShell
      number={number}
      sectionKey="executive"
      title={t("assetDashboardText13")}
      openLabel={t("assetDashboardText14")}
      closedLabel={t("assetDashboardText15")}
    >
      <div className={styles.stats}>
        {stats.map((item) => (
          <div key={item.key} className={styles.stat}>
            <span className={styles.stat_badge}>{item.badge}</span>
            <span className={styles.stat_label}>{t(item.labelKey)}</span>
            <span className={styles.stat_value}>{item.value}</span>
            <Delta delta={item.delta} up={item.up} invert={item.invert} />
          </div>
        ))}
      </div>

      <div className={styles.section_rule} />

      <div className={styles.alloc}>
        <div className={styles.alloc_block}>
          <div className={styles.block_label}>{t("assetDashboardText21")}</div>
          {/*
            The valued assets grouped by their financial exposure. An asset
            without one is not on this ring at all — there is no category to put
            it in — so the ring can be empty while the tiles above are not.
          */}
          {allocation.length === 0 ? (
            <div className={styles.alloc_empty}>{t("assetDashboardText47")}</div>
          ) : (
            <div className={styles.alloc_row}>
              <Donut label={t("assetDashboardText21")} slices={allocation} />
              {/* Each slice named with its own figure — never colour alone. */}
              <ul className={`${styles.legend} ${styles.legend_cols}`}>
                {allocation.map((slice) => (
                  <li key={slice.key} className={styles.legend_row}>
                    <span className={styles.legend_swatch} style={{ background: slice.color }} />
                    <span className={styles.legend_label}>{slice.name}</span>
                    <span className={styles.legend_value}>{slice.pct}%</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className={styles.alloc_block}>
          <div className={styles.block_label}>{t("assetDashboardText22")}</div>
          {/*
            Where the counted assets are, by how many are in each country rather
            than by what they are worth — three in Egypt against one in France
            is 75/25 whatever their values.
          */}
          {geography.rows.length === 0 ? (
            <div className={styles.alloc_empty}>{t("assetDashboardText47")}</div>
          ) : (
            <ul className={styles.bars}>
              {geography.rows.map((row) => (
                <li key={row.key} className={styles.bar_row}>
                  <span className={styles.bar_label}>{row.label}</span>
                  <span className={styles.bar_value}>{row.pct}%</span>
                  <span className={styles.bar_track}>
                    <span
                      className={styles.bar_fill}
                      style={{ width: `${(row.pct / widestGeo) * 100}%` }}
                      title={`${row.label} — ${row.pct}%`}
                    />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className={styles.section_rule} />

      <div className={styles.block_label}>{t("assetDashboardText23")}</div>
      <div className={styles.ratios}>
        {ratios.map((item) => (
          <div key={item.key} className={styles.ratio}>
            <span className={styles.ratio_label}>{t(item.labelKey)}</span>
            <span
              className={`${styles.ratio_value} ${
                item.tone === true
                  ? styles.delta_up
                  : item.tone === false
                  ? styles.delta_down
                  : ""
              }`}
            >
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

/* ======================================================== 2 wealth summary */

/*
  The net-worth trend. Its own small chart rather than the asset panel's: that
  one labels every point with a pill and dates its axis to the day, and this
  needs a bare line across five years.

  Fed from `valueTimelineOf` — the account's own valuations, one point per date
  any asset was valued on, each summing every asset's latest reading at or
  before that date. It was hardcoded for a long time: a five-year curve from
  €19.4M to €29.3M that drew itself identically for every account, including
  ones with no assets at all.
*/
function TrendChart({ points, label, currency }) {
  const W = 620;
  const H = 250;
  const M = { top: 16, right: 16, bottom: 30, left: 52 };
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const max = Math.max(...points.map((p) => p.value), 0) || 1;
  /*
    One reading is a position, not an evolution -- so it is drawn as the
    point it is, centred, with no line or area pretending at a direction.

    Worth drawing rather than refusing: an account whose values were all
    recorded on one day (a demo account, seeded in one go) had a panel
    saying nothing while the list beside it showed the figures. The reader
    can see WHEN it was taken and roughly what it was, which is everything
    a single reading has to say.
  */
  const single = points.length === 1;
  /*
    Enough labels to read the axis, not so many that the dates overlap. Every
    point is still plotted and still has its own tooltip — this thins only what
    is written underneath, keeping the first and the last.
  */
  const labelEvery = Math.ceil(points.length / 6);
  const x = (i) =>
    single ? M.left + plotW / 2 : M.left + (plotW * i) / Math.max(points.length - 1, 1);
  const y = (v) => M.top + plotH - (v / max) * plotH;
  const line = points.map((p, i) => `${i ? "L" : "M"} ${x(i)} ${y(p.value)}`).join(" ");
  const area = `${line} L ${x(points.length - 1)} ${M.top + plotH} L ${x(0)} ${M.top + plotH} Z`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  return (
    <svg className={styles.trend} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
      <defs>
        <linearGradient id="ostreon_report_trend" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={CHART_COLORS.blue} stopOpacity="0.22" />
          <stop offset="100%" stopColor={CHART_COLORS.blue} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={M.left} y1={y(v)} x2={M.left + plotW} y2={y(v)} stroke="#eef1f6" strokeWidth="1" />
          <text x={M.left - 10} y={y(v) + 4} textAnchor="end" fontSize="10" fill="#5b6b85">
            {formatCompactMoney(v, currency, 1)}
          </text>
        </g>
      ))}
      {!single && (
        <>
          <path d={area} fill="url(#ostreon_report_trend)" />
          <path
            d={line}
            fill="none"
            stroke={CHART_COLORS.blue}
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </>
      )}
      {points.map((p, i) => (
        <g key={p.label}>
          <circle cx={x(i)} cy={y(p.value)} r="4.5" fill={CHART_COLORS.blue} stroke="#ffffff" strokeWidth="2">
            <title>{`${p.label} — ${formatMoney(p.value, currency)}`}</title>
          </circle>
          {(i % labelEvery === 0 || i === points.length - 1) && (
            <text x={x(i)} y={H - 10} textAnchor="middle" fontSize="10" fill="#5b6b85">
              {p.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

export function WealthSummary({ number, range, rates }) {
  const t = useReportContent(FALLBACK);
  const userData = UserData();
  const nameOf = useExposureNames();
  const categoryName = useCategoryNames();

  /*
    The same financial-exposure grouping the Executive Summary ring draws, in
    the other shape the design gives it: a bar and an amount per exposure
    instead of a slice and a percentage. One calculation feeds both, so the two
    sections can never tell different stories about the same assets.

    Each row's change, and the total's, follow the report's window rules exactly
    as the tiles above do.
  */
  const { groups, total, change } = React.useMemo(() => {
    const assets = ((userData && userData.UserData) || {}).assets || [];
    const opts = {
      from: range?.from || "",
      till: range?.till || "",
      currency: (range && range.currency) || "eur",
      rates,
    };
    return exposureGroupsOf(
      assets,
      opts,
      ((userData && userData.UserData) || {}).financial_exposure_custom_options
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, range?.from, range?.till, range?.currency, rates]);

  /*
    The same figures again, cut by asset TYPE rather than by exposure.

    Not a second opinion on the first block: every valued asset has a type,
    while only some have an exposure, so the two totals are allowed to differ
    and the one below is the larger whenever something has been valued but
    never categorised. Both are measured by the same `aggregate`, so neither
    can disagree with the tiles above about a figure they share.
  */
  const categories = React.useMemo(() => {
    const assets = ((userData && userData.UserData) || {}).assets || [];
    return categoryGroupsOf(assets, {
      from: range?.from || "",
      till: range?.till || "",
      currency: (range && range.currency) || "eur",
      rates,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, range?.from, range?.till, range?.currency, rates]);

  const currency = (range && range.currency) || "eur";
  const widest = Math.max(...groups.map((g) => g.amount), 1);
  // Its own scale: each block's longest bar fills its own width.
  const widestCategory = Math.max(...categories.groups.map((g) => g.amount), 1);

  /*
    Net worth over time, from the account's own valuations: one point per date
    any asset was valued on, each the sum of every asset's latest reading at or
    before it. The same `valueTimelineOf` the asset sections chart, given every
    asset instead of one type's.
  */
  const trend = React.useMemo(() => {
    const assets = ((userData && userData.UserData) || {}).assets || [];
    return valueTimelineOf(assets, {
      from: range?.from || "",
      till: range?.till || "",
      currency,
      rates,
    }).map((point) => ({ label: point.date, value: point.value }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, range?.from, range?.till, currency, rates]);

  return (
    <SectionShell
      number={number}
      sectionKey="wealth"
      title={t("assetDashboardText33")}
      openLabel={t("assetDashboardText14")}
      /*
        The same key the other five sections use. Wealth Summary had one of its
        own, which read "Extend" / "Développer" where every other section said
        "Show" / "Afficher" -- one control, two different words for it. Sharing
        the key is what stops them drifting apart again.
      */
      closedLabel={t("assetDashboardText15")}
    >
      <div className={styles.alloc}>
        <div className={styles.alloc_block}>
          <div className={styles.block_label}>{t("assetDashboardText35")}</div>
          {groups.length === 0 ? (
            <div className={styles.alloc_empty}>{t("assetDashboardText47")}</div>
          ) : (
            <>
              <ul className={styles.net_rows}>
                {groups.map((row) => (
                  <li key={row.key} className={styles.net_row}>
                    <span className={styles.net_label}>{nameOf(row)}</span>
                    <Delta delta={formatChange(row.change)} up={changeDirection(row.change)} />
                    <span className={styles.net_value}>
                      {formatMoney(row.amount, currency)}
                    </span>
                    <span className={styles.bar_track}>
                      {/* Each exposure keeps the colour it has on the ring. */}
                      <span
                        className={styles.bar_fill}
                        style={{
                          width: `${(row.amount / widest) * 100}%`,
                          background: row.color,
                          borderRadius: "5px",
                        }}
                        title={`${nameOf(row)} — ${formatMoney(row.amount, currency)}`}
                      />
                    </span>
                  </li>
                ))}
              </ul>
              <div className={styles.net_total}>
                <span className={styles.net_total_label}>
                  {t("assetDashboardText37")}
                </span>
                <Delta delta={formatChange(change)} up={changeDirection(change)} />
                <span className={styles.net_total_value}>
                  {formatMoney(total, currency)}
                </span>
              </div>
            </>
          )}
        </div>

        <div className={styles.alloc_block}>
          <div className={styles.block_label}>{t("assetDashboardText36")}</div>
          {/*
            Two reasons there is no line, and they are not the same thing.

            A line needs two points, so one reading is not an evolution --
            but saying "no values recorded yet" when the block beside this
            one is listing totals contradicts it on the same screen. That is
            what a demo account looks like: everything seeded on one day, so
            one date, real figures, and a panel claiming there are none. A
            range that excludes every reading reads the same way, while the
            totals keep showing the last reading before it.

            So the first message is kept for an estate with nothing valued at
            all, and anything else says what is actually true: there are
            figures, just not enough of them in this period to draw a trend.
          */}
          {trend.length === 0 ? (
            <div className={styles.alloc_empty}>
              {total > 0 || categories.total > 0
                ? t("assetDashboardText110")
                : t("assetDashboardText47")}
            </div>
          ) : (
            <TrendChart
              points={trend}
              currency={currency}
              label={t("assetDashboardText36")}
            />
          )}
        </div>
      </div>

      {/*
        A row of its own, beneath the exposure one rather than wrapped in
        beside it: the two are the same reading of the estate cut two ways,
        and each gets the full width to be read across.
      */}
      <div className={styles.alloc}>
        {/*
          The same block again, by asset category.

          Categories are the asset TYPES the account holds -- built-in or one
          the user named themselves -- and a type nothing is valued under is
          not drawn at all: a category with no figure is an absence, not a
          zero. `categoryName` translates the built-ins and hands back a
          custom type's own name, which is what it is stored as.
        */}
        <div className={`${styles.alloc_block} ${styles.alloc_block_half}`}>
          <div className={styles.block_label}>{t("assetDashboardText109")}</div>
          {categories.groups.length === 0 ? (
            <div className={styles.alloc_empty}>{t("assetDashboardText47")}</div>
          ) : (
            <>
              <ul className={styles.net_rows}>
                {categories.groups.map((row) => (
                  <li key={row.key} className={styles.net_row}>
                    <span className={styles.net_label}>
                      {categoryName(row.key)}
                    </span>
                    <Delta delta={formatChange(row.change)} up={changeDirection(row.change)} />
                    <span className={styles.net_value}>
                      {formatMoney(row.amount, currency)}
                    </span>
                    <span className={styles.bar_track}>
                      <span
                        className={styles.bar_fill}
                        style={{
                          width: `${(row.amount / widestCategory) * 100}%`,
                          background: row.color,
                          borderRadius: "5px",
                        }}
                        title={`${categoryName(row.key)} \u2014 ${formatMoney(row.amount, currency)}`}
                      />
                    </span>
                  </li>
                ))}
              </ul>
              <div className={styles.net_total}>
                <span className={styles.net_total_label}>
                  {t("assetDashboardText37")}
                </span>
                <Delta
                  delta={formatChange(categories.change)}
                  up={changeDirection(categories.change)}
                />
                <span className={styles.net_total_value}>
                  {formatMoney(categories.total, currency)}
                </span>
              </div>
            </>
          )}
        </div>

      </div>
    </SectionShell>
  );
}

/* ==================================================== 3-6 asset categories */

/*
  One section per asset type the user actually holds something of.

  The design draws four fixed categories; in practice the sections are whatever
  the account contains, so they are generated. A section is a type, a row is an
  asset in it, and the share is of that section's own total — the section is the
  whole as far as the reader of it is concerned.

  An unvalued asset still gets a row. It belongs to the type, and saying so is
  the point: the row is what tells the user there is something left to fill in,
  and clicking it opens the panel to fill it.
*/
export function ExposureSection({ number, section, currency, onAddValue }) {
  const t = useReportContent(FALLBACK);
  const exposureName = useExposureNames();
  const title = exposureName(section);

  const chartSeries = [
    {
      key: "value",
      records: section.series,
      label: t("assetDashboardText44"),
      color: CHART_COLORS.blue,
      gradientId: `ostreon_report_exposure_${number}`,
    },
  ];

  return (
    <SectionShell
      sectionKey={section.key}
      number={number}
      title={title}
      openLabel={t("assetDashboardText14")}
      closedLabel={t("assetDashboardText15")}
    >
      <div className={styles.alloc}>
        <div className={styles.alloc_block}>
          <div className={styles.block_label}>{t("assetDashboardText42")}</div>
          <table className={styles.breakdown}>
            <tbody>
              {section.rows.map((row) => (
                <tr key={row.key}>
                  <td className={styles.bd_name}>
                    <span className={styles.bd_name_text} title={row.name}>
                      {row.name}
                    </span>
                  </td>
                  {row.amount === null ? (
                    /*
                      Nothing recorded yet. One cell across both columns rather
                      than a zero and a 0% share, which would read as "worth
                      nothing" instead of "not valued yet".
                    */
                    <td className={styles.bd_novalue} colSpan={2}>
                      {/*
                        Two different absences, said differently. "No value"
                        means the asset has never been priced; "no value in this
                        period" means it has, just not inside the dates being
                        looked at — so the reader knows whether they are seeing a
                        gap in their records or a consequence of the filter.
                      */}
                      <button
                        type="button"
                        className={`${styles.novalue_button} ${
                          row.everValued ? styles.novalue_period : ""
                        }`}
                        onClick={() => onAddValue(row.asset)}
                      >
                        {row.everValued
                          ? t("assetDashboardText98")
                          : t("assetDashboardText97")}
                      </button>
                    </td>
                  ) : (
                    <>
                      <td className={styles.bd_value}>
                        {formatMoney(row.amount, currency)}
                      </td>
                      <td className={styles.bd_pct}>{row.pct}%</td>
                    </>
                  )}
                  <td className={styles.bd_slack} />
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className={styles.bd_total_label}>{t("assetDashboardText43")}</td>
                <td className={styles.bd_total_value}>
                  {formatMoney(section.total, currency)}
                </td>
                <td className={styles.bd_pct}>{section.valued ? "100%" : "—"}</td>
                <td className={styles.bd_slack} />
              </tr>
            </tfoot>
          </table>
        </div>

        <div className={`${styles.alloc_block} ${styles.alloc_block_chart}`}>
          {section.series.length === 0 ? (
            <div className={styles.alloc_empty}>{t("assetDashboardText47")}</div>
          ) : (
            /* The asset panel's own chart, so the report and the modals agree. */
            <EvaluationChart
              series={chartSeries}
              currencyCode={currency}
              title={`${t("assetDashboardText44")} (${String(currency).toUpperCase()}) ${t("assetDashboardText45")}`}
              yAxisLabel={t("assetDashboardText44")}
              xAxisLabel={t("assetDashboardText46")}
              emptyText={t("assetDashboardText47")}
            />
          )}
        </div>
      </div>
    </SectionShell>
  );
}

/*
  The exposure sections as a group, numbered on from where the fixed sections
  stop. Held here rather than in the page so the numbering and the modal that
  belongs to them travel together.
*/
export function ExposureSections({ startNumber, range, rates, onSections }) {
  const userData = UserData();
  const exposureName = useExposureNames();
  const [valuing, setValuing] = useState(null);
  const currency = (range && range.currency) || "eur";

  const sections = React.useMemo(
    () =>
      exposureSectionsOf(
        ((userData && userData.UserData) || {}).assets || [],
        {
          from: range?.from || "",
          till: range?.till || "",
          currency,
          rates,
        },
        ((userData && userData.UserData) || {}).financial_exposure_custom_options
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userData, range?.from, range?.till, currency, rates]
  );

  /*
    Reported upward, because how many of these there are and what they are
    called is not known until the assets have been read — the page numbers the
    sections after them from the count, and the chapter picker lists them by
    name. Titles are resolved here, with the same helper the section header
    uses, so the picker and the header cannot disagree.
  */
  const listed = React.useMemo(
    () =>
      sections.map((section) => ({
        key: section.key,
        title: exposureName(section),
      })),
    [sections, exposureName]
  );

  useEffect(() => {
    if (onSections) onSections(listed);
  }, [listed, onSections]);

  return (
    <>
      {sections.map((section, i) => (
        <ExposureSection
          key={section.key}
          number={startNumber + i}
          section={section}
          currency={currency}
          onAddValue={setValuing}
        />
      ))}
      {valuing && (
        <ValuationModal asset={valuing} onClose={() => setValuing(null)} />
      )}
    </>
  );
}

/* ================================================================ 7 income */

/*
  Annual income, one row per asset that earns something.

  An asset with no income block is not in this section at all — it is not
  earning nothing, it has simply never been asked. The share is of the income
  total, not of the estate: this section answers "where does the money come
  from", and every row of it adds to 100%.

  The same date range as every other section: an asset contributes its income
  when it counts toward the report, so this describes the same estate the tiles
  above it do.
*/
export function IncomeSection({ number, range, rates }) {
  const t = useReportContent(FALLBACK);
  const userData = UserData();
  const currency = range?.currency || "eur";

  const income = useMemo(() => {
    const assets = (userData && userData.UserData && userData.UserData.assets) || [];
    return incomeBreakdownOf(assets, {
      from: range?.from,
      till: range?.till,
      currency,
      rates,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, range?.from, range?.till, currency, rates]);

  return (
    <SectionShell
      number={number}
      sectionKey="income"
      title={t("assetDashboardText48")}
      openLabel={t("assetDashboardText14")}
      closedLabel={t("assetDashboardText15")}
    >
      {income.rows.length === 0 ? (
        <div className={styles.alloc_empty}>{t("assetDashboardText99")}</div>
      ) : (
        <div className={styles.alloc}>
          <div className={styles.alloc_block}>
            <div className={styles.block_label}>{t("assetDashboardText49")}</div>
            <table className={styles.breakdown}>
              <tbody>
                {income.rows.map((row) => (
                  <tr key={row.key}>
                    <td className={styles.bd_name}>
                      <span className={styles.bd_name_text} title={row.name}>
                        {row.name}
                      </span>
                    </td>
                    <td className={styles.bd_value}>
                      {formatMoney(row.amount, currency)}
                    </td>
                    <td className={styles.bd_pct}>{row.pct}%</td>
                    <td className={styles.bd_slack} />
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className={styles.bd_total_label}>{t("assetDashboardText43")}</td>
                  <td className={styles.bd_total_value}>
                    {formatMoney(income.total, currency)}
                  </td>
                  <td className={styles.bd_pct}>100%</td>
                  <td className={styles.bd_slack} />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className={`${styles.alloc_block} ${styles.alloc_block_chart}`}>
            <div className={styles.block_label}>{t("assetDashboardText50")}</div>
            <div className={styles.alloc_row}>
              <Donut label={t("assetDashboardText50")} slices={income.rows} />
              <ul className={styles.legend}>
                {income.rows.map((row) => (
                  <li key={row.key} className={styles.legend_stacked}>
                    <span
                      className={styles.legend_swatch}
                      style={{ background: row.color }}
                    />
                    <span className={styles.legend_stack_text}>
                      <span className={styles.legend_label} title={row.name}>
                        {row.name}
                      </span>
                      <span className={styles.legend_sub}>
                        {formatMoney(row.amount, currency)} - {row.pct}%
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </SectionShell>
  );
}

/* ================================================================ 8 estate */

export function EstateSection({ number, range, rates }) {
  const t = useReportContent(FALLBACK);
  const websiteCurrentLang = useContext(LanguageContext).language;
  const userData = UserData();
  const currency = range?.currency || "eur";
  // The asset whose value is being filled in from a card; null when closed.
  const [valuing, setValuing] = useState(null);

  const sharing = useMemo(
    () =>
      sharingByBeneficiaryOf((userData && userData.UserData) || {}, {
        from: range?.from,
        till: range?.till,
        currency,
        rates,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userData, range?.from, range?.till, currency, rates]
  );

  return (
    <>
      <SectionShell
        number={number}
        sectionKey="estate"
        title={t("assetDashboardText55")}
        openLabel={t("assetDashboardText14")}
        closedLabel={t("assetDashboardText15")}
      >
        {/* The four stages, joined by the rule the numbers sit on. */}
        <ol className={styles.steps}>
          {ESTATE_STEPS.map((step, i) => (
            <li key={step.key} className={styles.step}>
              <span className={styles.step_num}>{i + 1}</span>
              <span className={styles.step_title}>{t(step.titleKey)}</span>
              <span className={styles.step_text}>{t(step.textKey)}</span>
            </li>
          ))}
        </ol>

        <div className={styles.section_rule} />

        <div className={styles.block_label}>{t("assetDashboardText74")}</div>
        <div className={styles.sharing}>
          {sharing.map((card) => {
            const photo = avatarSrc(card.avatar);
            return (
              <div key={card.key} className={styles.share_card}>
                {/*
                  "Mohamed (Partner)" where a relationship has been set, the
                  name alone where it has not — an empty bracket says less than
                  no bracket at all. The card's initials stay off the plain
                  name below, so the circle does not pick up the bracket.
                */}
                <div className={styles.share_person}>
                  {card.relationship
                    ? `${card.name} (${relationshipLabel(
                        card.relationship,
                        websiteCurrentLang?.label
                      )})`
                    : card.name}
                </div>
                <div className={styles.share_body}>
                  {photo ? (
                    <img className={styles.share_avatar} src={photo} alt="" />
                  ) : (
                    <span className={styles.share_avatar}>
                      {initialsOf(card.name)}
                    </span>
                  )}
                  <ul className={styles.share_items}>
                    {card.items.map((item) => (
                      <li key={item.key} className={styles.share_item}>
                        <span className={styles.share_item_name} title={item.name}>
                          {item.name}
                        </span>
                        {item.amount === null ? (
                          /*
                            Said the same way, and offering the same way out, as
                            an unpriced row in an asset section: which of the two
                            absences it is, and a click onto the valuation modal
                            to close it.
                          */
                          <button
                            type="button"
                            className={`${styles.share_item_value} ${
                              styles.novalue_button
                            } ${item.everValued ? styles.novalue_period : ""}`}
                            onClick={() => setValuing(item.asset)}
                          >
                            {item.everValued
                              ? t("assetDashboardText98")
                              : t("assetDashboardText97")}
                          </button>
                        ) : (
                          <span className={styles.share_item_value}>
                            {formatMoney(item.amount, currency)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
          </div>
      </SectionShell>

      {/*
        Outside the shell, so a card's "no value" still opens it when the reader
        has the section folded away — and so the modal is never photographed
        into the PDF along with the report.
      */}
      {valuing && (
        <ValuationModal asset={valuing} onClose={() => setValuing(null)} />
      )}
    </>
  );
}

/* ======================================================= 9 recommendations */

export function RecommendationsSection({ number }) {
  const t = useReportContent(FALLBACK);
  return (
    <SectionShell
      number={number}
      sectionKey="recommendations"
      title={t("assetDashboardText75")}
      openLabel={t("assetDashboardText14")}
      closedLabel={t("assetDashboardText15")}
    >
      <ol className={styles.recs}>
        {RECOMMENDATIONS.map((rec, i) => (
          <li key={rec.key} className={styles.rec}>
            <span className={styles.rec_num}>{i + 1}</span>
            <span className={styles.rec_title}>{t(rec.titleKey)}</span>
            <span className={styles.rec_text}>{t(rec.textKey)}</span>
          </li>
        ))}
      </ol>
    </SectionShell>
  );
}

